import { useEditorStore } from '../stores/useEditorStore';

import { type GenericElement, resetLayerCounter } from './element';
import { getPlugin, type Plugin } from './plugin';

export const roundExportVal = (value: number) => {
  return Math.round(value * 100) / 100;
};

export const prepareElementForExport = (element: Partial<GenericElement>) => {
  const exported = { ...element };

  delete exported.isDeletedFlag;
  delete exported.version;
  delete exported.shape;
  delete exported.originX;
  delete exported.originY;

  return {
    ...exported,
    x: roundExportVal(exported.x!),
    y: roundExportVal(exported.y!),
    width: roundExportVal(exported.width!),
    height: roundExportVal(exported.height!),
    angle: roundExportVal(exported.angle!),
  };
};

export const exportEditorElements = async () => {
  const state = useEditorStore.getState();
  const elements = Array.from(state.elements.values());

  return {
    elements: await Promise.all(
      elements
        .map((element) => {
          const plugin = getPlugin(element.type);
          return plugin.export?.(element);
        })
        .filter(Boolean),
    ).then((e) => e.filter(Boolean)),
  };
};

export const importEditorElements = async (
  plugins: Plugin[],
  payload: { elements: GenericElement[] },
  options?: { allLocked?: boolean },
) => {
  const { elements } = payload;

  const pluginMap = new Map<string, Plugin>(
    plugins.map((plugin) => [plugin.name, plugin]),
  );

  const iElements = await Promise.all(
    elements.map(async (element) => {
      const plugin = pluginMap.get(element.type);
      return await plugin?.import?.(element);
    }),
  );

  const importedElements = iElements.filter((element) => element !== undefined);

  const importedElementsMap = new Map<string, GenericElement>(
    (await Promise.all(importedElements))
      .map((element) => {
        if (!element) return;

        return [
          element.id,
          {
            ...element,
            isLocked: options?.allLocked ?? element.isLocked,
            originX: element.x,
            originY: element.y,
          },
        ] as const;
      })
      .filter(Boolean),
  );

  useEditorStore.setState({ elements: importedElementsMap });

  resetLayerCounter();
};
